// =====================================================
// 白板系统 - Excalidraw 编辑器页面
// 增量存储：元素 → DB | 图片 → Storage 桶
// =====================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CaptureUpdateAction, Excalidraw, MainMenu, WelcomeScreen, exportToBlob, reconcileElements, useHandleLibrary } from '@excalidraw/excalidraw';
import '@excalidraw/excalidraw/index.css';
import { Save, Check, AlertCircle, Loader2, RefreshCw, Share2, Users, BookmarkPlus } from 'lucide-react';
import { useWhiteboardStore } from '../stores/whiteboard-store';
import { useWhiteboardTabsStore } from '../stores/whiteboard-tabs-store';
import * as service from '../services/whiteboard-service';
import { deleteCachedProject, getCachedProject, putCachedProject } from '../services/whiteboard-cache';
import { supabase } from '@/config/supabase';
import { useCollaboration } from '../collab/useCollaboration';
import { ShareDialog } from '../components/ShareDialog';
import type { SaveStatus, WhiteboardCollaboratorMember, WhiteboardProject } from '../types';
import type { ExcalidrawImperativeAPI } from '@excalidraw/excalidraw/types';

const MAX_CONFLICT_AUTO_RETRIES = 5;
const CONFLICT_RETRY_BASE_MS = 800;
const CONFLICT_RETRY_MAX_MS = 8_000;
const CONFLICT_BURST_WINDOW_MS = 15_000;

function isRemoteNewer(remote: WhiteboardProject, local: WhiteboardProject): boolean {
  if (typeof remote.canvas_version === 'number' && typeof local.canvas_version === 'number') {
    return remote.canvas_version > local.canvas_version;
  }
  return remote.updated_at !== local.updated_at;
}

/**
 * 返回图片 fileId 加载顺序：视口内（含缓冲）优先，其余按离视口中心由近到远
 */
function getPrioritizedImageFileIds(elements: readonly any[], appState: any): string[] {
  const images = elements.filter((el: any) => el.type === 'image' && el.fileId && !el.isDeleted);
  if (images.length === 0) return [];

  const zoom = typeof appState?.zoom === 'object'
    ? appState.zoom.value || 1
    : (typeof appState?.zoom === 'number' ? appState.zoom : 1);
  const scrollX = typeof appState?.scrollX === 'number' ? appState.scrollX : 0;
  const scrollY = typeof appState?.scrollY === 'number' ? appState.scrollY : 0;
  const viewWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const viewHeight = typeof window !== 'undefined' ? window.innerHeight : 1080;

  // Excalidraw 的 scroll 是场景坐标：视口左上角 = -scroll，视口尺寸需除以 zoom
  const buffer = 200 / zoom;
  const minX = -scrollX - buffer;
  const minY = -scrollY - buffer;
  const maxX = -scrollX + viewWidth / zoom + buffer;
  const maxY = -scrollY + viewHeight / zoom + buffer;
  const centerX = -scrollX + viewWidth / zoom / 2;
  const centerY = -scrollY + viewHeight / zoom / 2;

  const ranked = images.map((el: any) => {
    const w = el.width || 0;
    const h = el.height || 0;
    const visible = el.x + w >= minX && el.x <= maxX && el.y + h >= minY && el.y <= maxY;
    const dist = Math.hypot(el.x + w / 2 - centerX, el.y + h / 2 - centerY);
    return { fileId: el.fileId as string, visible, dist };
  });
  ranked.sort((a, b) => Number(b.visible) - Number(a.visible) || a.dist - b.dist);

  return [...new Set(ranked.map((r) => r.fileId))];
}


// =====================================================
// 编辑器页面
// =====================================================

export function WhiteboardEditorPage({
  keepAliveId,
  isActive = true,
}: {
  keepAliveId?: string;
  isActive?: boolean;
} = {}) {
  const { id: paramId } = useParams<{ id: string }>();
  const id = keepAliveId || paramId;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get('token');
  // 切换标签会丢掉 URL 中的 token，这里锁存它，避免 token 变化触发重新加载
  const [shareToken, setShareToken] = useState<string | null>(urlToken);
  useEffect(() => {
    if (isActive && urlToken && urlToken !== shareToken) setShareToken(urlToken);
  }, [isActive, urlToken, shareToken]);
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;

  // 本地独立维护项目元数据，彻底杜绝多标签并发 KeepAlive 下的 currentProject 状态串扰
  const [localProject, setLocalProject] = useState<WhiteboardProject | null>(null);
  const currentProject = localProject;

  const setHasUnsavedChanges = useWhiteboardStore((s) => s.setHasUnsavedChanges);
  const setSaveCurrentCanvas = useWhiteboardStore((s) => s.setSaveCurrentCanvas);
  const setGetCanvasExportData = useWhiteboardStore((s) => s.setGetCanvasExportData);
  const loadProjects = useWhiteboardStore((s) => s.loadProjects);

  const excalidrawAPIRef = useRef<ExcalidrawImperativeAPI | null>(null);
  const [excalidrawAPI, setExcalidrawAPI] = useState<ExcalidrawImperativeAPI | null>(null);
  const [initialData, setInitialData] = useState<any>(null);
  const [isReady, setIsReady] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [isPinning, setIsPinning] = useState(false);
  const [members, setMembers] = useState<WhiteboardCollaboratorMember[]>([]);

  // 协作 Hook
  const {
    isCollaborating, isOwner, collaborators, onPointerUpdate,
    syncElements, startCollab, stopCollab, shareInfo,
    connectionStatus, connectionError, isApplyingRemoteScene,
    leaveCollab, removeCollaborator, validateAccess, endedReason, clearEndedReason,
  } = useCollaboration(id, excalidrawAPI, shareToken, currentProject);

  // ===== 协作 Ref：避免 Excalidraw 内部回调缓存导致闭包值陈旧 =====
  const isCollaboratingRef = useRef(false);
  isCollaboratingRef.current = isCollaborating;
  const syncElementsRef = useRef(syncElements);
  syncElementsRef.current = syncElements;
  const onPointerUpdateRef = useRef(onPointerUpdate);
  onPointerUpdateRef.current = onPointerUpdate;
  const isApplyingRemoteSceneRef = useRef(isApplyingRemoteScene);
  isApplyingRemoteSceneRef.current = isApplyingRemoteScene;
  const leaveCollabRef = useRef(leaveCollab);
  leaveCollabRef.current = leaveCollab;

  // 稳定的 onPointerUpdate 包装（引用永不变，内部读 ref）
  const stableOnPointerUpdate = useCallback((payload: any) => {
    onPointerUpdateRef.current?.(payload);
  }, []);

  // 保存状态 — 用 ref 存实际值，避免 setState 触发 Excalidraw 重渲染导致循环
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveStatusRef = useRef<SaveStatus>('saved');
  const lastSavedAtRef = useRef<Date | null>(null);
  const isSavingRef = useRef(false);
  const queuedSaveRef = useRef<{ elements: any[]; appState: any; files: any } | null>(null);
  const conflictRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const consecutiveConflictCountRef = useRef(0);
  const lastConflictAtRef = useRef(0);
  const doSaveRef = useRef<((elements: any[], appState: any, files: any) => Promise<void>) | null>(null);
  const canvasVersionRef = useRef<number | null>(null);
  const [, setRenderTick] = useState(0); // 仅用于触发 UI 更新
  const forceRender = useCallback(() => setRenderTick((t) => t + 1), []);

  // 安全地更新保存状态（不直接 setState，避免循环）
  const updateSaveStatus = useCallback((status: SaveStatus) => {
    saveStatusRef.current = status;
    forceRender();
  }, [forceRender]);

  // 增量图片跟踪
  const savedFileIdsRef = useRef<Set<string>>(new Set());
  const uploadingFileIdsRef = useRef<Set<string>>(new Set());
  const fileUploadRetryAfterRef = useRef<Map<string, number>>(new Map());
  const isInitializedRef = useRef(false);
  const pendingFilesRef = useRef<Record<string, any>>({});

  // 素材库持久化 — 跨设备同步（依赖 excalidrawAPI state，确保 API 就绪后才加载）
  useHandleLibrary({
    excalidrawAPI,
    adapter: {
      load: async () => ({ libraryItems: await service.loadLibrary() }),
      save: async (data) => { await service.saveLibrary([...data.libraryItems]); },
    },
  });

  // 项目 ID 守卫锁 — 防止切换时旧数据写入新项目
  const projectIdRef = useRef<string | null>(null);
  // 当前展示的项目记录（缓存或服务器），用于判断服务器版本是否更新
  const projectRef = useRef<WhiteboardProject | null>(null);

  // Tab Session ID — 每个标签页唯一标识，用于 Realtime 过滤自身保存
  const sessionIdRef = useRef(crypto.randomUUID());

  // 元素指纹（用于判断内容是否真正变化）
  const lastSavedKeyRef = useRef('');
  const getContentKey = (elements: readonly any[]) =>
    elements.filter((e: any) => !e.isDeleted).map((e: any) => `${e.id}:${e.version}`).join(',');

  // 图片按「离视口中心由近及远」排队，6 路并发，每张下载完立即上画布
  const loadImagesProgressively = useCallback((
    storageKey: string | null | undefined,
    elements: readonly any[],
    appState: any,
  ) => {
    if (!storageKey || elements.length === 0) return;
    const ids = getPrioritizedImageFileIds(elements, appState)
      .filter((fileId) => !savedFileIdsRef.current.has(fileId));
    if (ids.length === 0) return;
    const loadingId = projectIdRef.current;

    void service.loadFilesProgressive(
      storageKey,
      ids,
      (file) => {
        savedFileIdsRef.current.add(file.id);
        const api = excalidrawAPIRef.current;
        if (api) api.addFiles([file]);
        else pendingFilesRef.current[file.id] = file;
      },
      { shouldStop: () => projectIdRef.current !== loadingId },
    ).catch((err) => console.warn('[Whiteboard] 图片加载异常:', err));
  }, []);

  // ===== 加载项目（渐进式：矢量秒开 -> 图片按视口优先级并发补全） =====
  useEffect(() => {
    if (!id) return;
    projectIdRef.current = id;       // ← 标记当前活跃项目
    setInitialData(null);            // ← 立即清空，防止新 Excalidraw 实例用旧数据
    isInitializedRef.current = false;
    isSavingRef.current = false;
    queuedSaveRef.current = null;
    consecutiveConflictCountRef.current = 0;
    lastConflictAtRef.current = 0;
    pendingFilesRef.current = {};
    if (conflictRetryTimerRef.current) {
      clearTimeout(conflictRetryTimerRef.current);
      conflictRetryTimerRef.current = null;
    }
    savedFileIdsRef.current = new Set();
    uploadingFileIdsRef.current = new Set();
    fileUploadRetryAfterRef.current = new Map();
    saveStatusRef.current = 'saved';
    lastSavedAtRef.current = null;    // ← 清空旧时间

    // 把一份项目数据（缓存或服务器）显示到画布
    const applyProject = (project: WhiteboardProject) => {
      projectRef.current = project;
      setLocalProject(project);
      useWhiteboardTabsStore.getState().updateTabName(id, project.name);
      if (isActiveRef.current) {
        useWhiteboardStore.setState({ currentProject: project });
      }

      const canvasData = service.loadElements(project);
      const elements = canvasData.elements || [];
      const appState = canvasData.appState || {};

      // 矢量元素秒开：立即填充 elements 并解除 Loading
      setInitialData({ elements, appState, files: {} });
      lastSavedKeyRef.current = getContentKey(elements);
      lastSavedAtRef.current = project.updated_at ? new Date(project.updated_at) : null;
      canvasVersionRef.current = typeof project.canvas_version === 'number' ? project.canvas_version : null;
      setHasUnsavedChanges(false);
      setIsReady(true);

      // 数据就绪后延迟启用 onChange，等 Excalidraw 完成内部渲染
      setTimeout(() => {
        if (projectIdRef.current === id) {
          isInitializedRef.current = true;
        }
      }, 500);

      loadImagesProgressively(project.storage_key, elements, appState);
    };

    // 已显示缓存后，用服务器数据校验；有更新则合并进当前画布（不打断正在进行的编辑）
    const revalidate = async (project: WhiteboardProject) => {
      const previous = projectRef.current;
      projectRef.current = project;
      setLocalProject(project);
      useWhiteboardTabsStore.getState().updateTabName(id, project.name);
      if (isActiveRef.current) {
        useWhiteboardStore.setState({ currentProject: project });
      }
      if (previous && !isRemoteNewer(project, previous)) return;

      for (let i = 0; i < 30 && !excalidrawAPIRef.current; i++) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      const api = excalidrawAPIRef.current;
      if (!api || projectIdRef.current !== id) return;

      const remoteElements = service.loadElements(project).elements || [];
      const merged = reconcileElements(
        api.getSceneElementsIncludingDeleted(),
        remoteElements as any,
        api.getAppState(),
      );
      canvasVersionRef.current = typeof project.canvas_version === 'number' ? project.canvas_version : null;
      if (saveStatusRef.current === 'saved') {
        lastSavedKeyRef.current = getContentKey(merged);
        lastSavedAtRef.current = project.updated_at ? new Date(project.updated_at) : null;
      }
      api.updateScene({ elements: merged, captureUpdate: CaptureUpdateAction.NEVER });
      forceRender();
      loadImagesProgressively(project.storage_key, remoteElements, api.getAppState());
    };

    const load = async () => {
      setIsReady(false);

      const cached = await getCachedProject(id);
      if (projectIdRef.current !== id) return;
      if (cached) applyProject(cached);

      let project: WhiteboardProject | null;
      try {
        project = await service.fetchProjectById(id, shareToken);
      } catch (error) {
        // 离线或网络错误：已有缓存则继续使用缓存
        console.error('[Whiteboard] 加载项目失败:', error);
        if (!cached) setIsReady(true);
        return;
      }
      if (projectIdRef.current !== id) return;

      if (!project) {
        void deleteCachedProject(id);
        if (!cached) setIsReady(true);
        return;
      }
      void putCachedProject(project);

      // 收藏状态与画布无关，后台读取，不阻塞画布显示
      service.getWhiteboardAccessState(id)
        .then((access) => { if (projectIdRef.current === id) setIsPinned(access.isPinned); })
        .catch((error) => {
          console.warn('[Whiteboard] 无法读取协作收藏状态:', error);
          if (projectIdRef.current === id) setIsPinned(false);
        });

      if (cached) await revalidate(project);
      else applyProject(project);
    };

    load();

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      if (conflictRetryTimerRef.current) {
        clearTimeout(conflictRetryTimerRef.current);
        conflictRetryTimerRef.current = null;
      }
    };
  }, [id, forceRender, loadImagesProgressively, setHasUnsavedChanges, shareToken]);

  // ===== 激活状态感知与画布自适应刷新 =====
  useEffect(() => {
    if (isActive) {
      if (localProject) {
        useWhiteboardStore.setState({ currentProject: localProject });
      }
      setHasUnsavedChanges(saveStatusRef.current === 'unsaved');
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
        excalidrawAPIRef.current?.refresh();
      }, 50);
      return () => clearTimeout(timer);
    } else {
      // 切换到后台前，若有防抖保存未执行，立即执行
      if (autoSaveTimerRef.current && queuedSaveRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
        const queued = queuedSaveRef.current;
        queuedSaveRef.current = null;
        void doSaveRef.current?.(queued.elements, queued.appState, queued.files);
      }
    }
  }, [isActive, localProject, setHasUnsavedChanges]);

  useEffect(() => {
    if (!shareDialogOpen || !id || !isOwner || !isCollaborating) {
      if (!shareDialogOpen) setMembers([]);
      return;
    }
    let cancelled = false;
    void service.listWhiteboardCollaborators(id)
      .then((next) => { if (!cancelled) setMembers(next); })
      .catch((error) => console.error('[Whiteboard] 加载协作成员失败:', error));
    return () => { cancelled = true; };
  }, [id, isCollaborating, isOwner, shareDialogOpen]);

  const handlePinProject = useCallback(async () => {
    if (!id || isOwner || isPinned) return;
    setIsPinning(true);
    try {
      await service.setCollaborativeProjectPinned(id, true);
      setIsPinned(true);
      await loadProjects();
    } catch (error) {
      console.error('[Whiteboard] 添加到我的白板失败:', error);
    } finally {
      setIsPinning(false);
    }
  }, [id, isOwner, isPinned, loadProjects]);

  const handleRemoveMember = useCallback(async (userId: string) => {
    if (!id) return;
    await removeCollaborator(userId);
    setMembers(await service.listWhiteboardCollaborators(id));
  }, [id, removeCollaborator]);

  const handleStartCollaboration = useCallback(async () => {
    const link = await startCollab();
    if (link) await loadProjects();
    return link;
  }, [loadProjects, startCollab]);

  const handleStopCollaboration = useCallback(async () => {
    await stopCollab();
    await loadProjects();
  }, [loadProjects, stopCollab]);

  const handleAccessEndedConfirm = useCallback(async () => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    if (conflictRetryTimerRef.current) clearTimeout(conflictRetryTimerRef.current);
    queuedSaveRef.current = null;
    isInitializedRef.current = false;
    await leaveCollab();
    setHasUnsavedChanges(false);
    clearEndedReason();
    await loadProjects();
    navigate('/whiteboard', { replace: true });
  }, [clearEndedReason, leaveCollab, loadProjects, navigate, setHasUnsavedChanges]);

  // ===== 执行保存 =====
  const doSave = useCallback(
    async (elements: any[], appState: any, files: any) => {
      if (!id || id !== projectIdRef.current) return;

      // 保存期间的新编辑只覆盖队尾，当前写入完成后立即保存最新快照。
      if (isSavingRef.current) {
        queuedSaveRef.current = { elements, appState, files };
        return;
      }

      isSavingRef.current = true;
      updateSaveStatus('saving');
      let queuedSaveMode: 'immediate' | 'backoff' | 'stop' = 'immediate';
      let conflictRetryDelayMs = 0;

      try {
        const storagePath = localProject?.storage_key;

        // 先让图片落入 Storage；这样远端拿到 image element 后可以可靠地按 fileId 下载。
        if (files && Object.keys(files).length > 0 && storagePath) {
          const newIds = await service.saveFilesIncremental(storagePath, files, savedFileIdsRef.current);
          newIds.forEach((fileId) => savedFileIdsRef.current.add(fileId));
        }

        const savedVersion = await service.saveElements(id, {
          elements: [...elements],
          appState: {
            viewBackgroundColor: appState?.viewBackgroundColor,
            gridSize: appState?.gridSize,
            scrollX: appState?.scrollX,
            scrollY: appState?.scrollY,
            zoom: appState?.zoom,
          },
        }, sessionIdRef.current, {
          shareToken,
          expectedVersion: canvasVersionRef.current,
        });
        if (savedVersion !== null && Number.isFinite(savedVersion)) {
          canvasVersionRef.current = savedVersion;
        }
        consecutiveConflictCountRef.current = 0;
        lastConflictAtRef.current = 0;

        lastSavedAtRef.current = new Date();
        lastSavedKeyRef.current = getContentKey(elements);
        if (projectRef.current) {
          const snapshot: WhiteboardProject = {
            ...projectRef.current,
            canvas_data: {
              elements: [...elements],
              appState: {
                viewBackgroundColor: appState?.viewBackgroundColor,
                gridSize: appState?.gridSize,
                scrollX: appState?.scrollX,
                scrollY: appState?.scrollY,
                zoom: appState?.zoom,
              },
            },
            canvas_version: canvasVersionRef.current ?? projectRef.current.canvas_version,
            updated_at: lastSavedAtRef.current.toISOString(),
          };
          projectRef.current = snapshot;
          void putCachedProject(snapshot);
        }
        if (queuedSaveRef.current) {
          updateSaveStatus('unsaved');
          if (isActive) setHasUnsavedChanges(true);
          if (id) useWhiteboardTabsStore.getState().setTabDirty(id, true);
        } else {
          updateSaveStatus('saved');
          if (isActive) setHasUnsavedChanges(false);
          if (id) useWhiteboardTabsStore.getState().setTabDirty(id, false);
        }

        // 后台生成缩略图，不影响画布与图片文件的一致性。
        const activeElements = elements.filter((element: any) => !element.isDeleted);
        if (activeElements.length > 0 && storagePath) {
          void (async () => {
            try {
              const blob = await exportToBlob({
                elements: activeElements,
                files: files || null,
                mimeType: 'image/png',
                exportPadding: 16,
                getDimensions: (width: number, height: number) => {
                  const scale = Math.min(300 / width, 200 / height, 1);
                  return { width: width * scale, height: height * scale, scale };
                },
              });
              const bytes = new Uint8Array(await blob.arrayBuffer());
              await service.uploadFileToStorage(storagePath, 'thumbnail.png', bytes, 'image/png');
            } catch (error) {
              console.error('[Whiteboard] 缩略图生成失败:', error);
            }
          })();
        }
      } catch (error) {
        if (error instanceof service.WhiteboardVersionConflictError) {
          queuedSaveMode = 'backoff';
          try {
            const latestProject = await service.fetchProjectById(id, shareToken);
            if (!latestProject) throw error;

            canvasVersionRef.current = typeof latestProject.canvas_version === 'number'
              ? latestProject.canvas_version
              : null;
            const latestCanvas = service.loadElements(latestProject);
            const api = excalidrawAPIRef.current;
            const localElements = api?.getSceneElementsIncludingDeleted() || elements;
            const mergedElements = reconcileElements(
              localElements,
              latestCanvas.elements as any,
              api?.getAppState() || appState,
            );

            if (latestProject.storage_key && latestCanvas.elements.length) {
              const remoteFiles = await service.loadFiles(latestProject.storage_key, latestCanvas.elements);
              if (Object.keys(remoteFiles).length > 0) api?.addFiles(Object.values(remoteFiles));
            }
            api?.updateScene({ elements: mergedElements, captureUpdate: CaptureUpdateAction.NEVER });
            queuedSaveRef.current = {
              elements: [...mergedElements],
              appState: api?.getAppState() || appState,
              files: api?.getFiles() || files,
            };

            const now = Date.now();
            consecutiveConflictCountRef.current = now - lastConflictAtRef.current > CONFLICT_BURST_WINDOW_MS
              ? 1
              : consecutiveConflictCountRef.current + 1;
            lastConflictAtRef.current = now;

            if (consecutiveConflictCountRef.current <= MAX_CONFLICT_AUTO_RETRIES) {
              const exponentialDelay = Math.min(
                CONFLICT_RETRY_BASE_MS * (2 ** (consecutiveConflictCountRef.current - 1)),
                CONFLICT_RETRY_MAX_MS,
              );
              conflictRetryDelayMs = exponentialDelay + Math.floor(Math.random() * 300);
              updateSaveStatus('unsaved');
              if (isActive) setHasUnsavedChanges(true);
              if (id) useWhiteboardTabsStore.getState().setTabDirty(id, true);
            } else {
              queuedSaveMode = 'stop';
              queuedSaveRef.current = null;
              console.error('[Whiteboard] 并发保存冲突过多，已停止自动重试');
              updateSaveStatus('error');
              if (isActive) setHasUnsavedChanges(true);
              if (id) useWhiteboardTabsStore.getState().setTabDirty(id, true);
            }
          } catch (conflictError) {
            queuedSaveMode = 'stop';
            queuedSaveRef.current = null;
            console.error('[Whiteboard] 合并并发保存失败:', conflictError);
            updateSaveStatus('error');
            if (isActive) setHasUnsavedChanges(true);
            if (id) useWhiteboardTabsStore.getState().setTabDirty(id, true);
          }
        } else {
          queuedSaveMode = 'stop';
          queuedSaveRef.current = null;
          if (!(await validateAccess())) return;
          console.error('[Whiteboard] 保存失败:', error);
          updateSaveStatus('error');
          if (isActive) setHasUnsavedChanges(true);
          if (id) useWhiteboardTabsStore.getState().setTabDirty(id, true);
        }
      } finally {
        isSavingRef.current = false;
        const queued = queuedSaveRef.current;
        queuedSaveRef.current = null;
        if (queued && id === projectIdRef.current) {
          if (queuedSaveMode === 'backoff') {
            if (conflictRetryTimerRef.current) clearTimeout(conflictRetryTimerRef.current);
            conflictRetryTimerRef.current = setTimeout(() => {
              conflictRetryTimerRef.current = null;
              void doSaveRef.current?.(queued.elements, queued.appState, queued.files);
            }, conflictRetryDelayMs);
          } else if (queuedSaveMode === 'immediate') {
            queueMicrotask(() => { void doSaveRef.current?.(queued.elements, queued.appState, queued.files); });
          }
        }
      }
    },
    [id, isActive, localProject?.storage_key, setHasUnsavedChanges, shareToken, updateSaveStatus, validateAccess]
  );
  doSaveRef.current = doSave;

  // ===== onChange（3 秒防抖自动保存） =====
  const handleChange = useCallback(
    (elements: readonly any[], appState: any, files: any) => {
      if (!isInitializedRef.current) return;
      if (!id || id !== projectIdRef.current) return;
      if (isApplyingRemoteSceneRef.current()) return;

      // 指纹对比：内容未变则跳过
      const currentKey = getContentKey(elements);
      if (currentKey === lastSavedKeyRef.current) return;

      // 内容真正变化了
      if (saveStatusRef.current !== 'unsaved') {
        saveStatusRef.current = 'unsaved';
        if (isActive) setHasUnsavedChanges(true);
        if (id) useWhiteboardTabsStore.getState().setTabDirty(id, true);
        forceRender();
      }

      // 协作模式：同步元素到其他协作者（用 ref 读最新值，避免 Excalidraw 回调缓存问题）
      if (isCollaboratingRef.current) {
        const storagePath = localProject?.storage_key;
        const unsavedFileIds = files
          ? Object.keys(files).filter((fileId) => !savedFileIdsRef.current.has(fileId))
          : [];
        const now = Date.now();
        const uploadableFileIds = unsavedFileIds.filter((fileId) => (
          !uploadingFileIdsRef.current.has(fileId)
          && (fileUploadRetryAfterRef.current.get(fileId) || 0) <= now
        ));

        if (uploadableFileIds.length > 0 && storagePath) {
          const uploadFiles = Object.fromEntries(
            uploadableFileIds.map((fileId) => [fileId, files[fileId]]),
          );
          uploadableFileIds.forEach((fileId) => uploadingFileIdsRef.current.add(fileId));

          // 图片不再塞进 Broadcast。先上传成功，再广播带 fileId 的元素引用。
          void service.saveFilesIncremental(storagePath, uploadFiles, savedFileIdsRef.current)
            .then((newIds) => {
              newIds.forEach((fileId) => {
                savedFileIdsRef.current.add(fileId);
                fileUploadRetryAfterRef.current.delete(fileId);
              });
              syncElementsRef.current(elements as any);
            })
            .catch((error) => {
              const retryAfter = Date.now() + 10_000;
              uploadableFileIds.forEach((fileId) => {
                fileUploadRetryAfterRef.current.set(fileId, retryAfter);
              });
              console.error('[Whiteboard] 协作图片上传失败:', error);
              updateSaveStatus('error');
            })
            .finally(() => {
              uploadableFileIds.forEach((fileId) => uploadingFileIdsRef.current.delete(fileId));
            });
        } else if (unsavedFileIds.length === 0) {
          // 有图片仍在上传或处于失败退避时，不提前广播 image element，避免远端灰色占位。
          syncElementsRef.current(elements as any);
        }
      }

      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = setTimeout(() => {
        doSave([...elements], appState, files);
      }, 3000);
    },
    [id, isActive, localProject?.storage_key, doSave, forceRender, setHasUnsavedChanges, updateSaveStatus]
  );

  // ===== 手动保存 =====
  const handleManualSave = useCallback(() => {
    if (!id || !excalidrawAPIRef.current) return;

    const elements = excalidrawAPIRef.current.getSceneElementsIncludingDeleted();
    const appState = excalidrawAPIRef.current.getAppState();
    const files = excalidrawAPIRef.current.getFiles();

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    if (conflictRetryTimerRef.current) {
      clearTimeout(conflictRetryTimerRef.current);
      conflictRetryTimerRef.current = null;
    }
    consecutiveConflictCountRef.current = 0;
    lastConflictAtRef.current = 0;
    return doSave([...elements], appState, files);
  }, [id, doSave]);

  // 注册保存回调和导出数据回调到 store（仅当激活时）
  useEffect(() => {
    if (!isActive) return;

    const saveFn = async () => { await handleManualSave(); };
    setSaveCurrentCanvas(saveFn);

    const exportFn = () => {
      if (!excalidrawAPIRef.current) return { elements: [], appState: {}, files: {} };
      return {
        elements: [...excalidrawAPIRef.current.getSceneElements()],
        appState: excalidrawAPIRef.current.getAppState(),
        files: excalidrawAPIRef.current.getFiles(),
      };
    };
    setGetCanvasExportData(exportFn);

    return () => {
      setSaveCurrentCanvas(null);
      setGetCanvasExportData(null);
    };
  }, [isActive, handleManualSave, setSaveCurrentCanvas, setGetCanvasExportData]);

  // ===== Ctrl+S 快捷键（仅激活时响应） =====
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleManualSave();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, handleManualSave]);

  // ===== 跨设备实时同步 — Supabase Realtime（Session ID 过滤） =====
  const [remoteUpdate, setRemoteUpdate] = useState<{ time: Date } | null>(null);

  useEffect(() => {
    if (!id) return;
    console.log('[Realtime] 订阅白板变更:', id, '| sessionId:', sessionIdRef.current);
    const channel = supabase
      .channel(`whiteboard-sync-${id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'whiteboard_projects', filter: `id=eq.${id}` },
        (payload: any) => {
          const remoteSession = payload.new?.last_save_session;
          console.log('[Realtime] 收到变更事件, remote session:', remoteSession, '| local session:', sessionIdRef.current);
          // 精确过滤：如果是当前标签页自己的保存，忽略
          if (remoteSession && remoteSession === sessionIdRef.current) {
            console.log('[Realtime] 忽略（本标签页保存）');
            return;
          }
          // 所有者关闭协作后，即使 Broadcast 恰好丢失，仍通过持久化状态退出本地会话。
          if (isCollaboratingRef.current && payload.new?.share_mode !== 'edit') {
            console.log('[Realtime] 协作已被所有者关闭，退出频道');
            void leaveCollabRef.current();
            return;
          }
          // 协作模式下不弹窗：实时同步已由 Broadcast 处理
          if (isCollaboratingRef.current) {
            console.log('[Realtime] 协作模式中，跳过弹窗提示');
            return;
          }
          setRemoteUpdate({ time: new Date() });
        }
      )
      .subscribe((status: string, err?: Error) => {
        console.log('[Realtime] 订阅状态:', status, err || '');
      });

    return () => {
      console.log('[Realtime] 取消订阅:', id);
      supabase.removeChannel(channel);
    };
  }, [id]);

  // 刷新白板（接受远程更新，渐进式秒开）
  const handleAcceptRemote = useCallback(async () => {
    if (!id) return;
    setRemoteUpdate(null);
    setIsReady(false);
    excalidrawAPIRef.current = null;
    isInitializedRef.current = false;
    const project = await service.fetchProjectById(id, shareToken);
    if (!project) { setIsReady(true); return; }
    projectRef.current = project;
    void putCachedProject(project);
    savedFileIdsRef.current = new Set();
    setLocalProject(project);
    useWhiteboardTabsStore.getState().updateTabName(id, project.name);
    const canvasData = service.loadElements(project);
    const elements = canvasData.elements || [];
    const appState = canvasData.appState || {};

    // 立即秒开矢量元素
    setInitialData({
      elements,
      appState,
      files: {},
    });
    lastSavedKeyRef.current = getContentKey(elements);
    lastSavedAtRef.current = project.updated_at ? new Date(project.updated_at) : null;
    canvasVersionRef.current = typeof project.canvas_version === 'number' ? project.canvas_version : null;
    setHasUnsavedChanges(false);
    setIsReady(true);
    setTimeout(() => { isInitializedRef.current = true; }, 500);

    loadImagesProgressively(project.storage_key, elements, appState);
  }, [id, loadImagesProgressively, setHasUnsavedChanges, shareToken]);

  const collaborationBadge = connectionStatus === 'connected'
    ? { label: '协作中', className: 'bg-green-100 text-green-700', dotClassName: 'bg-green-500 animate-pulse' }
    : connectionStatus === 'connecting' || connectionStatus === 'reconnecting'
      ? { label: '正在重连', className: 'bg-amber-100 text-amber-700', dotClassName: 'bg-amber-500 animate-pulse' }
      : { label: '协作已断开', className: 'bg-red-100 text-red-700', dotClassName: 'bg-red-500' };

  // 无 ID 重定向
  if (!id) { navigate('/whiteboard'); return null; }

  // 加载中
  if (!isReady) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-gray-500">加载白板中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* 远程更新 — 强制确认弹窗（仅在激活标签页显示） */}
      {remoteUpdate && isActive && (
        <div className="fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center" style={{ backdropFilter: 'blur(2px)' }}>
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4 animate-in zoom-in-95">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                <RefreshCw className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">白板内容已更新</h3>
                <p className="text-xs text-gray-500 mt-0.5">其他设备于 {remoteUpdate.time.toLocaleTimeString('zh-CN')} 保存了新版本</p>
              </div>
            </div>
            <p className="text-xs text-gray-600 mb-5 leading-relaxed">
              检测到此白板在其他设备上被修改。你可以刷新加载最新内容，或忽略此次更新继续编辑当前版本。
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleAcceptRemote}
                className="flex-1 h-9 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors"
              >
                刷新加载
              </button>
              <button
                onClick={() => setRemoteUpdate(null)}
                className="flex-1 h-9 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                忽略继续
              </button>
            </div>
          </div>
        </div>
      )}

      {endedReason && (
        <div className="fixed inset-0 z-[10000] bg-black/45 flex items-center justify-center" style={{ backdropFilter: 'blur(2px)' }}>
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4 animate-in zoom-in-95">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">协作权限已失效</h3>
                <p className="text-xs text-gray-500 mt-1">
                  {endedReason === 'closed'
                    ? '创建者已关闭该白板的协作权限。'
                    : '你已被创建者移出该协作白板。'}
                </p>
              </div>
            </div>
            <button
              onClick={() => void handleAccessEndedConfirm()}
              className="w-full h-9 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors"
            >
              确认并返回我的白板
            </button>
          </div>
        </div>
      )}

      {/* 分享对话框 */}
      <ShareDialog
        isOpen={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        isCollaborating={isCollaborating}
        isOwner={isOwner}
        createdBy={currentProject?.created_by}
        collaborators={collaborators}
        shareInfo={shareInfo}
        onStartCollab={handleStartCollaboration}
        onStopCollab={handleStopCollaboration}
        members={members}
        onRemoveMember={handleRemoveMember}
      />

      {/* 顶部状态栏 */}
      <div className="h-10 bg-white border-b border-gray-200 px-4 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-gray-700 truncate max-w-[300px]">
            {currentProject?.name || '未命名白板'}
          </div>
          {isCollaborating && (
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full ${collaborationBadge.className}`}
              title={connectionError || collaborationBadge.label}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${collaborationBadge.dotClassName}`} />
              {collaborationBadge.label}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {!isOwner && isCollaborating && (
            <button
              onClick={() => void handlePinProject()}
              disabled={isPinning || isPinned}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium transition-all ${
                isPinned
                  ? 'bg-blue-50 text-blue-600 cursor-default'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              } disabled:opacity-70`}
            >
              {isPinning
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <BookmarkPlus className="w-3.5 h-3.5" />}
              {isPinning ? '添加中' : isPinned ? '已添加' : '添加到我的白板'}
            </button>
          )}
          {/* 协作者头像 */}
          {isCollaborating && collaborators.size > 1 && (
            <div className="flex items-center -space-x-1.5">
              {[...collaborators.values()]
                .filter(u => !u.isCurrentUser)
                .slice(0, 4)
                .map((user) => (
                  <div
                    key={user.userId}
                    className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-[10px] font-bold text-white shrink-0"
                    style={{ backgroundColor: user.color }}
                    title={user.username}
                  >
                    {user.avatarUrl ? (
                      <img src={user.avatarUrl} alt={user.username} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      user.username.charAt(0).toUpperCase()
                    )}
                  </div>
                ))}
              {[...collaborators.values()].filter(u => !u.isCurrentUser).length > 4 && (
                <div className="w-6 h-6 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-[9px] font-medium text-gray-600 shrink-0">
                  +{[...collaborators.values()].filter(u => !u.isCurrentUser).length - 4}
                </div>
              )}
            </div>
          )}

          {/* 分享按钮 */}
          <button
            onClick={() => setShareDialogOpen(true)}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium transition-all ${
              isCollaborating
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {isCollaborating ? <Users className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
            <span>{isCollaborating
              ? (connectionStatus === 'connected' ? `${collaborators.size} 人` : '重连中')
              : '分享'}
            </span>
          </button>

          {/* 保存按钮 */}
          <button
            onClick={handleManualSave}
            disabled={saveStatusRef.current === 'saving'}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium transition-all ${saveStatusRef.current === 'saving'
              ? 'bg-blue-50 text-blue-400 cursor-wait'
              : saveStatusRef.current === 'unsaved' || saveStatusRef.current === 'error'
                ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
          >
            {saveStatusRef.current === 'saving'
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Save className="w-3.5 h-3.5" />
            }
            <span>{saveStatusRef.current === 'saving' ? '保存中' : '保存'}</span>
          </button>

          {/* 状态标签 */}
          <div className={`flex items-center gap-1.5 text-[11px] ${saveStatusRef.current === 'saved' ? 'text-green-600' :
            saveStatusRef.current === 'unsaved' ? 'text-amber-600' :
              saveStatusRef.current === 'saving' ? 'text-blue-500' :
                'text-red-500'
            }`}>
            {saveStatusRef.current === 'saved' && <><Check className="w-3 h-3" /><span>{lastSavedAtRef.current ? `已保存 ${lastSavedAtRef.current.toLocaleTimeString('zh-CN')}` : '已保存'}</span></>}
            {saveStatusRef.current === 'unsaved' && <><span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" /><span>已修改</span></>}
            {saveStatusRef.current === 'saving' && <><Loader2 className="w-3 h-3 animate-spin" /><span>保存中...</span></>}
            {saveStatusRef.current === 'error' && <><AlertCircle className="w-3 h-3" /><span>保存失败</span></>}
          </div>
        </div>
      </div>

      {/* Excalidraw 编辑器 — key={id} 强制切换项目时重新挂载 */}
      <div style={{ width: '100%', height: '100%', flex: 1, position: 'relative', overflow: 'hidden' }}>
        <Excalidraw
          key={id}
          excalidrawAPI={(api) => {
            excalidrawAPIRef.current = api;
            setExcalidrawAPI(api);
            // 若在 API 挂载完成前已有下载好的图片文件，立刻追加到画布
            if (Object.keys(pendingFilesRef.current).length > 0) {
              api.addFiles(Object.values(pendingFilesRef.current));
            }
            // 不在此处设 isInitializedRef — 由 load() 完成后设置，防止数据未就绪时触发 onChange
          }}
          initialData={initialData || undefined}
          onChange={handleChange}
          onLibraryChange={async (items) => await service.saveLibrary([...items])}
          isCollaborating={isCollaborating}
          onPointerUpdate={isCollaborating ? stableOnPointerUpdate : undefined}
          langCode="zh-CN"
          theme="light"
        >
          <MainMenu>
            <MainMenu.DefaultItems.LoadScene />
            <MainMenu.DefaultItems.Export />
            <MainMenu.DefaultItems.SaveAsImage />
            <MainMenu.DefaultItems.ClearCanvas />
            <MainMenu.DefaultItems.ToggleTheme />
            <MainMenu.DefaultItems.ChangeCanvasBackground />
          </MainMenu>
          <WelcomeScreen>
            <WelcomeScreen.Center>
              <WelcomeScreen.Center.Heading>
                开始绘制你的白板
              </WelcomeScreen.Center.Heading>
            </WelcomeScreen.Center>
          </WelcomeScreen>
        </Excalidraw>
      </div>
    </div>
  );
}
